<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});

const inboxes = useMapGetter('inboxes/getInboxes');

const inboxesList = computed(() => {
  const sorted =
    inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name)) || [];
  if (isAdmin.value) return sorted;
  return sorted.filter(inbox => inbox.current_user_inbox_role === 'admin');
});

const canConfigureInbox = inbox => {
  return isAdmin.value || inbox.current_user_inbox_role === 'admin';
};

const connectionStatusConfig = status => {
  const configs = {
    connected: {
      class: 'bg-n-teal-9',
      label: t('INBOX_MGMT.STATUS.CONNECTED'),
    },
    disconnected: {
      class: 'bg-n-ruby-9',
      label: t('INBOX_MGMT.STATUS.DISCONNECTED'),
    },
    qr_pending: {
      class: 'bg-n-amber-9',
      label: t('INBOX_MGMT.STATUS.QR_PENDING'),
    },
    connecting: {
      class: 'bg-n-amber-9',
      label: t('INBOX_MGMT.STATUS.CONNECTING'),
    },
    needs_reauth: {
      class: 'bg-n-ruby-9',
      label: t('INBOX_MGMT.STATUS.NEEDS_REAUTH'),
    },
  };
  return configs[status] || configs.disconnected;
};

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const tableHeaders = computed(() => [
  t('INBOX_MGMT.LIST.TABLE_HEADER.INBOX'),
  '',
]);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <SettingsPageLayout
    :title="$t('INBOX_MGMT.HEADER')"
    :description="$t('INBOX_MGMT.DESCRIPTION')"
  >
    <template #actions>
      <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
        <Button
          icon="i-lucide-circle-plus"
          :label="$t('SETTINGS.INBOXES.NEW_INBOX')"
        />
      </router-link>
    </template>

    <SettingsTable
      :headers="tableHeaders"
      :is-loading="uiFlags.isFetching"
      :is-empty="!inboxesList.length"
      :empty-message="$t('INBOX_MGMT.LIST.404')"
    >
      <tr
        v-for="inbox in inboxesList"
        :key="inbox.id"
        class="hover:bg-n-solid-3 transition-colors"
      >
        <td class="px-5 py-3">
          <div class="flex items-center flex-row gap-3">
            <div class="relative">
              <div
                v-if="inbox.avatar_url"
                class="bg-n-alpha-3 rounded-full size-10 p-1.5 ring ring-n-solid-1 border border-n-strong shadow-sm flex items-center justify-center"
              >
                <Avatar
                  :src="inbox.avatar_url"
                  :name="inbox.name"
                  :size="28"
                  rounded-full
                />
              </div>
              <div
                v-else
                class="size-10 flex justify-center items-center bg-n-alpha-3 rounded-full p-1.5 ring ring-n-solid-1 border border-n-strong shadow-sm"
              >
                <ChannelIcon class="size-4 text-n-slate-10" :inbox="inbox" />
              </div>
              <span
                v-if="inbox.connection_status"
                v-tooltip.top="
                  connectionStatusConfig(inbox.connection_status).label
                "
                class="absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-n-solid-2"
                :class="connectionStatusConfig(inbox.connection_status).class"
              />
            </div>
            <div class="min-w-0">
              <span class="block text-sm font-medium text-n-slate-12 truncate">
                {{ inbox.name }}
              </span>
              <div class="flex items-center gap-2">
                <ChannelName
                  class="text-xs text-n-slate-10"
                  :channel-type="inbox.channel_type"
                  :medium="inbox.medium"
                />
                <span v-if="inbox.phone_number" class="text-xs text-n-slate-9">
                  {{ inbox.phone_number }}
                </span>
              </div>
            </div>
          </div>
        </td>

        <td class="px-5 py-3">
          <div class="flex items-center gap-2 justify-end">
            <span
              v-if="inbox.connection_status"
              class="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full"
              :class="{
                'bg-n-teal-3 text-n-teal-11':
                  inbox.connection_status === 'connected',
                'bg-n-ruby-3 text-n-ruby-11':
                  inbox.connection_status === 'disconnected' ||
                  inbox.connection_status === 'needs_reauth',
                'bg-n-amber-3 text-n-amber-11':
                  inbox.connection_status === 'connecting' ||
                  inbox.connection_status === 'qr_pending',
              }"
            >
              <span
                class="size-1.5 rounded-full"
                :class="connectionStatusConfig(inbox.connection_status).class"
              />
              {{ connectionStatusConfig(inbox.connection_status).label }}
            </span>
            <div class="flex gap-1">
              <router-link
                :to="{
                  name: 'settings_inbox_show',
                  params: { inboxId: inbox.id },
                }"
              >
                <Button
                  v-if="canConfigureInbox(inbox)"
                  v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                  icon="i-lucide-settings"
                  slate
                  xs
                  faded
                />
              </router-link>
              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                @click="openDelete(inbox)"
              />
            </div>
          </div>
        </td>
      </tr>
    </SettingsTable>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsPageLayout>
</template>
